import { Injectable, inject } from '@angular/core';
import { doc, getDoc, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore';

import { ProductAccessConfig } from '../models/product.model';
import { FirebaseService } from './firebase.service';

/** The product id for Plan (this app) in the org product-access config. */
export const PLAN_PRODUCT_ID = 'plan';

/**
 * Pure access evaluation, identical across the ecosystem (Forge, Chat, …):
 * - no config            → no access (Plan is disabled until explicitly enabled)
 * - product not enabled  → no access
 * - role restricted list → role must include the product
 */
export function evaluateAccess(
  config: ProductAccessConfig | null,
  roleId: string | null,
  productId: string,
): boolean {
  if (!config) return false;
  if (!config.enabledProducts.includes(productId)) return false;
  if (roleId) {
    const roleProducts = config.roleProductAccess[roleId];
    if (Array.isArray(roleProducts) && roleProducts.length > 0 && !roleProducts.includes(productId)) {
      return false;
    }
  }
  return true;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly firebase = inject(FirebaseService);

  async getProductAccessConfig(orgId: string): Promise<ProductAccessConfig> {
    const cacheKey = `crewdle_product_access_${orgId}`;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) return JSON.parse(stored) as ProductAccessConfig;

    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'product-access', 'config');
    const snapshot = await getDoc(configRef);
    const config: ProductAccessConfig = snapshot.exists()
      ? {
          enabledProducts: snapshot.data()['enabledProducts'] || [],
          roleProductAccess: snapshot.data()['roleProductAccess'] || {},
        }
      : // No config doc = legacy org — default to Forge only
        { enabledProducts: ['ai-studio'], roleProductAccess: {} };

    sessionStorage.setItem(cacheKey, JSON.stringify(config));
    return config;
  }

  /**
   * Subscribe to product access config changes. Fires once on attach with
   * the current Firestore value, then again on every remote update (e.g.
   * agent-driven toggles). Updates the sessionStorage cache on each fire.
   */
  subscribeToConfig(orgId: string, onChange: (config: ProductAccessConfig) => void): Unsubscribe {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'product-access', 'config');
    return onSnapshot(configRef, (snapshot) => {
      const config: ProductAccessConfig = snapshot.exists()
        ? {
            enabledProducts: snapshot.data()['enabledProducts'] || [],
            roleProductAccess: snapshot.data()['roleProductAccess'] || {},
          }
        : { enabledProducts: ['ai-studio'], roleProductAccess: {} };
      sessionStorage.setItem(`crewdle_product_access_${orgId}`, JSON.stringify(config));
      onChange(config);
    });
  }

  /** Whether the org (and the given role) currently has access to a product. */
  async checkProductAccess(orgId: string, productId: string, roleId: string | null): Promise<boolean> {
    const config = await this.getProductAccessConfig(orgId);
    return evaluateAccess(config, roleId, productId);
  }

  async saveProductAccessConfig(orgId: string, config: ProductAccessConfig): Promise<void> {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'product-access', 'config');
    await setDoc(configRef, {
      enabledProducts: config.enabledProducts,
      roleProductAccess: config.roleProductAccess,
    });
    sessionStorage.removeItem(`crewdle_product_access_${orgId}`);
  }
}
